/**
 * Expandable ball-skin selector. Collapsed it's a compact "current skin" chip; tap
 * it and the full choice grid expands (animated) below. Picking a skin selects it
 * and collapses back to the chip.
 *
 * Presentational only — it owns just its open/closed UI state (human-timescale,
 * not per-frame) and reports selections up via the unchanged `onSelect` prop.
 */
import { useState } from 'react';
import type { Skin } from '../game/skins';
import { SkinPreview } from './SkinPreview';

interface SkinPickerProps {
  skins: readonly Skin[];
  selectedId: string;
  onSelect: (id: string) => void;
}

export function SkinPicker({ skins, selectedId, onSelect }: SkinPickerProps) {
  const [open, setOpen] = useState(false);
  const current = skins.find((s) => s.id === selectedId) ?? skins[0];

  const choose = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div className="skin-picker-wrap" data-open={open ? 'true' : 'false'}>
      {/* Collapsed: the current-skin chip that toggles the grid. */}
      <button
        type="button"
        className="skin-chip glass"
        aria-expanded={open}
        aria-controls="skin-grid"
        onClick={() => setOpen((v) => !v)}
      >
        {current && <SkinPreview skin={current} className="skin-svg skin-chip-art" />}
        <span className="skin-chip-text">
          <span className="skin-chip-name">{current?.name ?? 'Skin'}</span>
          <span className="skin-chip-hint">{open ? 'Tap to close' : 'Tap to change'}</span>
        </span>
        <span className="skin-chip-caret" aria-hidden>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded: the full choice grid (animated open/closed via [data-open]). */}
      <div id="skin-grid" className="skin-picker-panel" role="region" aria-hidden={!open}>
        <div className="skin-picker">
          {skins.map((skin) => {
            const selected = skin.id === selectedId;
            return (
              <button
                key={skin.id}
                type="button"
                className={selected ? 'skin-swatch selected' : 'skin-swatch'}
                aria-pressed={selected}
                aria-label={skin.name}
                onClick={() => choose(skin.id)}
              >
                <SkinPreview skin={skin} className="skin-svg" />
                <span className="skin-name">{skin.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
